import { Icons } from "assets";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import ConfirmationModal from "shared/modal/confirmation";
import { resetAuthReducer } from "shared/redux/reducers/authSlice";
import { LogoutService } from "shared/services/auth";
import { toastMessage } from "../toast";
import NavItem from "./navItem";
import styles from "./style.module.scss";
import Switch from "../switch";
import { resetTheme, setTheme } from "shared/redux/reducers/theme";
import { resetCountReducer } from "shared/redux/reducers/count";
import { resetQuestionnaireReducer } from "shared/redux/reducers/questionnaire";

interface SideCanvasProps {
  setIsOpen: (val: boolean) => void;
  isOpen: boolean;
  sidebarArr: SideBarItemsType[];
}

const DashboardSideCanvas = ({
  isOpen,
  setIsOpen,
  sidebarArr,
}: SideCanvasProps) => {
  const {
    auth: { user },
    theme: { theme },
  } = useSelector((state: any) => state?.root);
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const [openConfirmationModal, setOpenConfirmationModal] =
    useState<boolean>(false);

  const handleOpenConfirmationModal = () => {
    setOpenConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setOpenConfirmationModal(false);
  };

  const handleLogout = () => {
    setLoading(true);
    LogoutService()
      .then(() => {
        dispatch(resetTheme());
        dispatch(resetAuthReducer());
        dispatch(resetCountReducer());
        dispatch(resetQuestionnaireReducer());
        toastMessage("success", "Logged out successfully");
      })
      .catch((err) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  function handleClick(e: any) {
    const elem: any = document.getElementById("sideCanvas");
    if (!elem.contains(e.target)) {
      setIsOpen(false);
    }
  }

  useEffect(() => {
    let elem: any = document.getElementById("backDropContainer");
    elem.addEventListener("click", (event: any) => {
      handleClick(event);
    });

    // eslint-disable-next-line
  }, [location.pathname]);

  return (
    <div
      className={classNames(styles.backDropContainer, "md:hidden")}
      style={isOpen ? { visibility: "visible" } : { visibility: "hidden" }}
      id="backDropContainer"
    >
      <div
        className={classNames(
          styles.mainContainer,
          isOpen ? styles.shown : styles.hidden
        )}
        id="sideCanvas"
      >
        <div
          className={classNames(
            "flex justify-between items-center px-4 gap-4",
            styles.logoContainer
          )}
        >
          <Icons.LogoABR />
          <div
            className={classNames(styles.crossContainer)}
            onClick={() => {
              setIsOpen(false);
            }}
          >
            <Icons.Cross />
          </div>
        </div>
        <div
          className={classNames(
            "flex items-center gap-2",
            styles.userInfoContainer
          )}
        >
          <div className={classNames(styles.avtInitials)}>
            <label>
              {user?.name?.split(" ")?.[0]?.[0]}
              {user?.name?.split(" ")?.[1]?.[0]}
              {user?.name?.split(" ")?.[2]?.[0]}
            </label>
          </div>
          <div className={classNames("flex flex-col items-start")}>
            <p>{user?.name}</p>
          </div>
        </div>
        <div className={classNames("flex flex-col gap-4 my-6")}>
          <div className="flex flex-col gap-4 ">
            {sidebarArr?.map((item, inx) => {
              return (
                <div className="flex flex-col items-start gap-3" key={inx}>
                  <label className={classNames(styles.navTitle)}>
                    {item?.title}
                  </label>

                  <div className="w-full">
                    {item?.paths?.map(
                      (s: SideBarItemPathType, s_inx: number) => {
                        const props = {
                          ...s,
                          setIsOpen,
                        };
                        return <NavItem {...props} key={s_inx} />;
                      }
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div
            className={classNames(
              "flex flex-col gap-3",
              styles.settingsContainer
            )}
          >
            <label className={classNames(styles.navTitle)}>Settings</label>

            <div className={classNames(styles.navItemContainer, "gap-[12px]")}>
              <label className={classNames(styles.themeTitle)}>
                {theme} Theme
              </label>
              <Switch
                toggle={theme === "dark"}
                handleChange={() => {
                  if (theme === "dark") {
                    dispatch(setTheme({ theme: "light" }));
                  } else {
                    dispatch(setTheme({ theme: "dark" }));
                  }
                }}
              />
            </div>
          </div>
          <div
            className={classNames(styles.navItemContainer)}
            onClick={handleOpenConfirmationModal}
          >
            <Icons.Logout className={classNames(styles.logoutIcon)} />
            <label
              className={classNames(
                styles.heading,
                styles.logout,
                "cursor-pointer"
              )}
            >
              Logout
            </label>
          </div>
        </div>
      </div>
      <ConfirmationModal
        show={openConfirmationModal}
        handleClose={handleCloseConfirmationModal}
        title="Are you sure you want to log out?"
        actionButtonText="Yes, Logout"
        handleSubmit={handleLogout}
        loading={loading}
        showSubtitle={false}
      />
    </div>
  );
};

export default DashboardSideCanvas;
